import { useEffect, useRef, type ReactNode } from 'react';

/**
 * An in-place «точно?» — the pair of buttons that replaces (or covers) the
 * control you just pressed.
 *
 * Two of these existed, and both dropped the keyboard user. Swapping a Delete
 * button for a Yes/No pair destroys the focused element, so focus falls back to
 * the document and the next Tab starts from the top of the page — the very
 * buttons the app is waiting on are the hardest thing to reach. The emoji one
 * was worse still: the confirm covers the row, so the answer was invisible to a
 * screen reader AND unreachable.
 *
 * So: name the group and put focus on the first action. Nothing here is
 * specific to emoji or invites — a third caller should use this rather than
 * repeat the pattern.
 *
 * Focus is handed back on close only when the element that had it still exists.
 * That covers the overlay case (the trigger stays mounted underneath). Where
 * the trigger is REPLACED by the confirm — the invite row — it is already gone
 * by the time this mounts, so there is nothing to capture and nothing to
 * restore; a test pins that this degrades quietly instead of throwing.
 *
 * Not a dialog: it does not cover the app and must not claim to. `group` with a
 * name is what a small cluster of related controls is.
 */
export function InlineConfirm(props: {
  /** Names the cluster, e.g. «Удалить эмодзи “blob”?». */
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    // The first button is the affirmative one at both call sites, which is the
    // one you came for. Cancel stays one Tab away — and Escape/click-away is
    // handled by the callers that have it.
    ref.current?.querySelector('button')?.focus();
    return () => {
      // Only if it is still in the document. A detached node cannot take focus,
      // and `document.body` (what `activeElement` reports when the trigger was
      // replaced) would take it and go nowhere useful.
      if (previous && previous !== document.body && previous.isConnected) previous.focus();
    };
  }, []);

  return (
    <div ref={ref} role="group" aria-label={props.label} className={props.className}>
      {props.children}
    </div>
  );
}
